"use client";

import Script from "next/script";
import { useEffect, useState } from "react";

import { AGE_GATE_COOKIE, AGE_GATE_UNLOCK_EVENT } from "@/constants/ageGate";
import { getCbgCompPackConfig } from "@/utilities/cbgCompPackConfig";

export type CbgCompPackScriptProps = {
  /** Draft preview is for editors; skip compliance plugin there. */
  layoutMode: "draft" | "published";
  /** From `age_gate_choice` cookie; delays Ottilia until the age gate is dismissed. */
  initialAgeGateChoice?: "yes" | "no" | null;
};

function hasAgeGateYesCookie(): boolean {
  return document.cookie
    .split(";")
    .some((part) => part.trim().startsWith(`${AGE_GATE_COOKIE}=yes`));
}

/**
 * Carlsberg Compliance Plugin (Ottilia). Loads after the age gate is passed.
 *
 * @see https://ottilia.carlsberggroup.com/plugin-docs
 */
export function CbgCompPackScript({
  layoutMode,
  initialAgeGateChoice = null,
}: CbgCompPackScriptProps) {
  const config = getCbgCompPackConfig();
  const [ageGatePassed, setAgeGatePassed] = useState(() => {
    if (initialAgeGateChoice === "yes") return true;
    if (typeof document !== "undefined" && hasAgeGateYesCookie()) return true;
    return false;
  });

  useEffect(() => {
    const onUnlock = () => setAgeGatePassed(true);
    window.addEventListener(AGE_GATE_UNLOCK_EVENT, onUnlock);
    return () => window.removeEventListener(AGE_GATE_UNLOCK_EVENT, onUnlock);
  }, []);

  if (layoutMode !== "published" || !config || !ageGatePassed) {
    return null;
  }

  return (
    <Script
      data-cookies-page={config.cookiesPage}
      data-gtm-id={config.gtmId}
      data-market={config.market}
      data-policy-page={config.policyPage}
      data-prj-id={config.projectId}
      data-verify-age={config.verifyAge ? "true" : "false"}
      id="cbgCompPack"
      src={config.src}
      strategy="afterInteractive"
    />
  );
}
